/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';
import isNil from 'lodash/isNil';

// Lib
import { apiClient } from '@lib/api-client';

// Types
import {
	TParsedSite,
	TApiResponseGetSitesPopulated,
	TApiResponseSitePopulated,
} from '../types';
import { TParams } from '@global-types/index';
import { TApiParams } from '@global-types/app/app';

// Utils
import {
	convertProduction,
	findClosestStartDate,
	formatDate,
	getRatioLabel,
} from '@utils/functions';
import { getUnitLabel } from '@utils/units';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getSites = (
	params: TApiParams
): Promise<TApiResponseGetSitesPopulated> => {
	const { queryParams, sortParams, page, pageSize } =
		params;
	return apiClient.get('/sites', {
		params: {
			page: page,
			pageSize: pageSize,
			query: queryParams,
			sort: sortParams,
			populate: [
				'project',
				'entity',
				'organization',
				'conversionFactors',
				'meterPoints',
			],
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useSitesApi = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { data, isFetching, isFetched } = useQuery({
		queryKey: [
			'sites',
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getSites({
				page: page,
				pageSize: size,
				queryParams: queryParams,
				sortParams: sortParams,
			}),
	});

	const updateList = (
		sites: TApiResponseSitePopulated[]
	) =>
		sites.reduce(
			(
				acc: TParsedSite[],
				currentItem: TApiResponseSitePopulated
			) => {
				const _conversionFactors =
					!isNil(currentItem) &&
					!isNil(currentItem.conversionFactors) &&
					Array.isArray(currentItem.conversionFactors) &&
					currentItem.conversionFactors.length > 0
						? currentItem.conversionFactors
						: [];

				const _minValue =
					!isNil(currentItem) &&
					!isNil(currentItem.production) &&
					!isNil(currentItem.production.minValue)
						? `${convertProduction(currentItem.production.minValue, currentItem.unit)}`
						: NOT_APPLICABLE;

				const _maxValue =
					!isNil(currentItem) &&
					!isNil(currentItem.production) &&
					!isNil(currentItem.production.maxValue)
						? `${convertProduction(currentItem.production.maxValue, currentItem.unit)}`
						: NOT_APPLICABLE;

				const _meterPointsCount =
					!isNil(currentItem) &&
					!isNil(currentItem.meterPoints) &&
					Array.isArray(currentItem.meterPoints) &&
					currentItem.meterPoints.length > 0
						? currentItem.meterPoints.length
						: 0;

				const closetConversionFactor =
					findClosestStartDate(_conversionFactors);

				const _lastConversionFactor = getRatioLabel(
					closetConversionFactor.ratio,
					closetConversionFactor.unit
				);

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const modifiedItem: TParsedSite = {
					...currentItem,
					project: !isNil(currentItem.project)
						? currentItem.project
						: NOT_APPLICABLE,
					projectName:
						!isNil(currentItem.project) &&
						!isNil(currentItem.project.name)
							? currentItem.project.name
							: NOT_APPLICABLE,
					organizationName:
						!isNil(currentItem.organization) &&
						!isNil(currentItem.organization.name)
							? currentItem.organization.name
							: NOT_APPLICABLE,
					organization:
						!isNil(currentItem.organization) &&
						!isNil(currentItem.organization)
							? currentItem.organization
							: NOT_APPLICABLE,
					producer: currentItem.entity
						? currentItem.entity
						: NOT_APPLICABLE,
					producerName:
						!isNil(currentItem.entity) &&
						!isNil(currentItem.entity.name)
							? currentItem.entity.name
							: NOT_APPLICABLE,
					minValueConverted: _minValue,
					maxValueConverted: _maxValue,
					closetConversionFactor,
					lastConversionFactor: _lastConversionFactor,
					meterPointsCount: _meterPointsCount,
					unit: getUnitLabel(
						currentItem.unit.toLowerCase()
					),
					createdAt: formatDate(
						currentItem.createdAt,
						'DD/MM/YYYY'
					),
					place:
						currentItem?.location?.place ||
						NOT_APPLICABLE,
				};
				acc.push(modifiedItem);

				return acc;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
